import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { createFormatters, type Formatters, type Lang } from './format';
import { createTranslator, type MessageKey, type Messages, type Translation, type Vars } from './messages';

export interface I18n<T extends Messages = Messages> {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  t: (key: MessageKey<T>, vars?: Vars) => string;
  fmt: Formatters;
}

const Context = createContext<I18n | null>(null);

export const dirOf = (lang: Lang): 'ltr' | 'rtl' => (lang === 'ar' ? 'rtl' : 'ltr');

/**
 * Supplies t() and the formatters for `lang`, and mirrors the page: <html lang dir>. The
 * language itself is app state (stored, synced to the user's profile) — pass it in.
 */
export function I18nProvider<T extends Messages>({
  lang,
  messages,
  children,
}: {
  lang: Lang;
  messages: { en: T; ar: Translation<T> };
  children: ReactNode;
}) {
  const value = useMemo<I18n<T>>(
    () => ({ lang, dir: dirOf(lang), t: createTranslator(lang, messages), fmt: createFormatters(lang) }),
    [lang, messages],
  );

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dirOf(lang);
  }, [lang]);

  return <Context.Provider value={value as unknown as I18n}>{children}</Context.Provider>;
}

/** Typed access: `const { t } = useI18n<AdminMessages>()`. */
export function useI18n<T extends Messages = Messages>(): I18n<T> {
  const value = useContext(Context);
  if (!value) throw new Error('useI18n() outside <I18nProvider>');
  return value as unknown as I18n<T>;
}
